import { Editor, Operation, Path, Range, Text, Transforms } from 'vendor/slate'

import { HistoryEditor, SPLITTING_ONCE } from './history-editor'

/**
 * The `withHistory` plugin keeps track of the operation history of a Slate
 * editor as operations are applied to it, using undo and redo stacks.
 *
 * If you are using TypeScript, you must extend Slate's CustomTypes to use
 * this plugin.
 *
 * See https://docs.slatejs.org/concepts/11-typescript to learn how.
 */

// A batch's `selectionBefore` is captured before normalization runs. Later
// normalization (e.g. removing an empty sibling text node when a marked text
// node is inserted) can shift paths, leaving `selectionBefore` pointing at a
// path that no longer exists. Restoring it then triggers a stale-path lookup in
// `Node.get`. Only restore the selection when its paths are still present.
const selectionIsInDocument = (e: Editor, selection): boolean => {
  if (!selection || !Range.isRange(selection)) {
    return false
  }

  return (
    Editor.hasPath(e, selection.anchor.path) &&
    Editor.hasPath(e, selection.focus.path)
  )
}

// History records paths at write time; normalization and comment splits can
// collapse text children later, so undo/redo may replay ops at missing paths.
const canApplyOp = (editor: Editor, op: Operation): boolean => {
  if ('path' in op && Path.isPath(op.path) && !Editor.hasPath(editor, op.path)) {
    return false
  }

  if (op.type === 'move_node' && !Editor.hasPath(editor, op.newPath)) {
    return false
  }

  return true
}

export const withHistory = <T extends Editor>(editor: T) => {
  const e = editor as T & HistoryEditor
  const { apply } = e
  e.history = { undos: [], redos: [] }

  e.resetHistory = () => {
    e.history = { undos: [], redos: [] };
  };

  e.redo = () => {
    const { history } = e
    const { redos } = history

    if (redos.length > 0) {
      const batch = redos[redos.length - 1]

      // Slate replaces `children`/`selection` with fresh immutable values on
      // every op (immer createDraft/finishDraft), so these references stay
      // frozen and serve as a clean pre-redo rollback point.
      const snapshotChildren = e.children
      const snapshotSelection = e.selection

      try {
        if (selectionIsInDocument(e, batch.selectionBefore)) {
          Transforms.setSelection(e, batch.selectionBefore)
        }

        HistoryEditor.withoutSaving(e, () => {
          Editor.withoutNormalizing(e, () => {
            for (const op of batch.operations) {
              if (canApplyOp(e, op)) {
                e.apply(op)
              }
            }
          })
        })

        history.redos.pop()
        history.undos.push(batch)
      } catch (error) {
        // The batch could not be cleanly re-applied/normalized. Roll back to the
        // pre-redo tree so we never leave a half-applied block on screen, and
        // drop the bad batch so redo isn't permanently wedged replaying it.
        e.children = snapshotChildren
        e.selection = snapshotSelection
        e.operations = []
        history.redos.pop()
        e.onChange()
      }
    }
  }

  e.undo = () => {
    const { history } = e
    const { undos } = history

    if (undos.length > 0) {
      const batch = undos[undos.length - 1]

      // Slate replaces `children`/`selection` with fresh immutable values on
      // every op (immer createDraft/finishDraft), so these references stay
      // frozen and serve as a clean pre-undo rollback point.
      const snapshotChildren = e.children
      const snapshotSelection = e.selection

      try {
        HistoryEditor.withoutSaving(e, () => {
          Editor.withoutNormalizing(e, () => {
            const inverseOps = batch.operations.map(Operation.inverse).reverse()

            for (const op of inverseOps) {
              if (canApplyOp(e, op)) {
                e.apply(op)
              }
            }
            if (selectionIsInDocument(e, batch.selectionBefore)) {
              Transforms.setSelection(e, batch.selectionBefore)
            }
          })
        })

        history.redos.push(batch)
        history.undos.pop()
      } catch (error) {
        // Applying or normalizing the inverse batch failed (a stale path, or a
        // normalizer that can't stabilize). Roll back to the pre-undo tree so we
        // never render a half-reverted block (the outdent), and still drop the
        // bad batch so undo isn't permanently wedged replaying it.
        e.children = snapshotChildren
        e.selection = snapshotSelection
        e.operations = []
        history.undos.pop()
        e.onChange()
      }
    }
  }

  e.apply = (op: Operation) => {
    const { operations, history } = e
    const { undos } = history
    const lastBatch = undos[undos.length - 1]
    const lastOp =
      lastBatch && lastBatch.operations[lastBatch.operations.length - 1]
    let save = HistoryEditor.isSaving(e)
    let merge = HistoryEditor.isMerging(e)

    if (save == null) {
      save = shouldSave(op, lastOp)
    }

    if (save) {
      if (merge == null) {
        if (lastBatch == null) {
          merge = false
        } else if (operations.length !== 0) {
          merge = true
        } else {
          merge = shouldMerge(op, lastOp)
        }
      }

      if (HistoryEditor.isSplittingOnce(e)) {
        merge = false
        SPLITTING_ONCE.delete(e)
      }

      if (lastBatch && merge) {
        lastBatch.operations.push(op)
      } else {
        const batch = {
          operations: [op],
          selectionBefore: e.selection,
        }
        undos.push(batch)
      }

      while (undos.length > 100) {
        undos.shift()
      }

      history.redos = []
    }

    apply(op)
  }

  return e
}

/**
 * Check whether to merge an operation into the previous operation.
 */

const shouldMerge = (op: Operation, prev: Operation | undefined): boolean => {
  if (
    prev &&
    op.type === 'insert_text' &&
    prev.type === 'insert_text' &&
    op.offset === prev.offset + prev.text.length &&
    Path.equals(op.path, prev.path)
  ) {
    return true
  }

  if (
    prev &&
    op.type === 'remove_text' &&
    prev.type === 'remove_text' &&
    op.offset + op.text.length === prev.offset &&
    Path.equals(op.path, prev.path)
  ) {
    return true
  }

  // Marked typing starts with insert_node (a text node carrying the active
  // marks, e.g. { text: 'h', color: '#f00' }), then continues with insert_text.
  // Merge them so undo removes the whole run instead of leaving the first char.
  // Require the node to actually carry marks and be non-empty so we don't merge
  // typing into an unrelated batch that ended by inserting an empty text node
  // (e.g. the { text: '' } created when an element/block is inserted) — that
  // would make a single undo pull the element out along with the character.
  if (
    prev &&
    prev.type === 'insert_node' &&
    op.type === 'insert_text' &&
    Text.isText(prev.node) &&
    prev.node.text.length > 0 &&
    Object.keys(prev.node).length > 1 &&
    Path.equals(op.path, prev.path) &&
    op.offset === prev.node.text.length
  ) {
    return true
  }

  return false
}

/**
 * Check whether an operation needs to be saved to the history.
 */

const shouldSave = (op: Operation, prev: Operation | undefined): boolean => {
  if (op.type === 'set_selection') {
    return false
  }

  return true
}
